import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {Subscription} from 'rxjs';
import {OperationContainer} from '../../core/definitions/operation-container';
import {OperationTarget} from '../../core/definitions/operation-target.enum';
import {OperationStepType} from '../../core/definitions/operation-step-type.enum';
import {SearchContainer} from '../../core/definitions/search-container';
import {Selector} from '../../core/definitions/reference';
import {SelectorCreationParams} from '../../core/definitions/selector-creation-params';
import {SelectorContainer} from '../../core/definitions/selector-container';
import {SearchSelectorService} from '../../core/search-selector.service';
import {SearchTemplateGroupService} from '../../object-search/search-template-group.service';
import {OperationDialogService} from '../../operations/operation-dialog.service';
import {OperationService} from '../../operations/operation.service';
import {ContentListChoiceService} from '../content-list-choice.service';
import {
  applyContentListFieldTemplateResult,
  CONTENT_LIST_FIELD_TEMPLATE_GROUP_ID,
  ContentListFieldTemplateActions,
  ContentListFieldTemplateResult,
  showsSelfHostedFieldSelector,
} from '../content-list-field-template';

@Component({
  selector: 'app-content-list-field-templates',
  templateUrl: './content-list-field-templates.component.html',
  standalone: false
})
export class ContentListFieldTemplatesComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly contentListChoice = inject(ContentListChoiceService);
  private readonly operationDialogService = inject(OperationDialogService);
  private readonly operationService = inject(OperationService);
  private readonly overlay = inject(Overlay);
  private readonly searchSelectorService = inject(SearchSelectorService);
  private readonly searchTemplateGroupService = inject(SearchTemplateGroupService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private selectorChanges: Subscription;
  private fieldSelectorOverlay: OverlayRef | null = null;
  private viewReady = false;

  @ViewChild('fieldSelectorPortal') fieldSelectorPortal: TemplateRef<unknown>;

  @Input() searchContainer: SearchContainer;
  @Input() reloadOverview: () => Promise<void>;

  readonly groupId = CONTENT_LIST_FIELD_TEMPLATE_GROUP_ID;
  operationContainer: OperationContainer;

  ngOnInit(): void {
    this.operationContainer = new OperationContainer(OperationTarget.CONTENT_LIST_VIEW);
    this.operationContainer.hostsOwnSelector = true;
    this.operationContainer.searchContainer = this.searchContainer;
    this.selectorChanges = this.searchSelectorService.searchSelectorChanged$.subscribe(container => {
      if (container === this.operationContainer || container == null) {
        this.changeDetector.markForCheck();
        this.placeFieldSelector();
      }
    });
    const parentObject = this.searchContainer.targetObject;
    if (parentObject) {
      this.operationContainer.art = parentObject;
      this.operationService.setOperationContextObjects(this.operationContainer, [parentObject]);
    }
    this.operationService.attachSelectorCallback(this.operationContainer);
    this.keepSelectionOnTheList();
    this.operationContainer.openOperationDialogFn = (callback) => {
      this.operationDialogService.openOperationDialog(this.operationContainer).then(data => {
        this.applyResult(data?.resultObject).then(() => {
          if (callback) {
            callback(data);
          }
        });
      });
    };
    this.operationContainer.enableSelector = (selector: Selector, params: SelectorCreationParams) => {
      this.openFieldSelector(selector, params);
    };
    this.operationService.setOperations(this.operationContainer, this.searchContainer).then();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.placeFieldSelector();
  }

  ngOnDestroy(): void {
    this.selectorChanges?.unsubscribe();
    this.detachFieldSelector();
  }

  get showFieldSelector(): boolean {
    return showsSelfHostedFieldSelector(this.operationContainer);
  }

  closeFieldSelector(): void {
    this.searchSelectorService.disableSelector(this.operationContainer);
  }

  // The object page draws the menu and the media in side panels above the list.
  // The field selector is attached to the page overlay so those panels sit behind it.
  private placeFieldSelector(): void {
    if (!this.viewReady) {
      return;
    }
    if (!this.showFieldSelector) {
      this.detachFieldSelector();
      return;
    }
    if (this.fieldSelectorOverlay?.hasAttached()) {
      return;
    }
    this.fieldSelectorOverlay = this.overlay.create({
      hasBackdrop: false,
      disposeOnNavigation: true,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position().global().top('0').left('0'),
      width: '100%',
      height: '100%',
      panelClass: 'content-list-field-selector-pane',
    });
    this.fieldSelectorOverlay.attach(new TemplatePortal(this.fieldSelectorPortal, this.viewContainerRef));
  }

  private detachFieldSelector(): void {
    this.fieldSelectorOverlay?.dispose();
    this.fieldSelectorOverlay = null;
  }

  private keepSelectionOnTheList(): void {
    const runSelection = this.operationContainer.selectorCallback;
    this.operationContainer.selectorCallback = (selectedObj, selector, callback, objIds) => {
      const stepType = this.operationContainer.currentOperation?.$$currentStep?.operation_step_type;
      runSelection(selectedObj, selector, (result) => {
        const finished = stepType === OperationStepType.SELECT_EXISTING_OBJECTS
          ? this.applyResult(selectedObj?.[0])
          : Promise.resolve(false);
        finished.then(() => {
          if (callback) {
            callback(result);
          }
        });
      }, objIds);
    };
  }

  private openFieldSelector(selector: Selector, params: SelectorCreationParams): void {
    this.operationContainer.contentInfo.setSelectorUsed(selector);
    this.searchSelectorService.enableSelector(
      selector,
      this.operationContainer as SelectorContainer,
      params,
      {
        searchContainerCreated: () => undefined,
        selectorCallback: (selectedObj) => {
          this.searchSelectorService.disableSelector(this.operationContainer as SelectorContainer);
          this.operationContainer.selectorCallback(selectedObj, selector);
        }
      }
    );
  }

  private applyResult(resultObject: ContentListFieldTemplateResult): Promise<boolean> {
    return applyContentListFieldTemplateResult(
      this.searchContainer, resultObject, this.fieldTemplateActions());
  }

  private fieldTemplateActions(): ContentListFieldTemplateActions {
    return {
      setListFieldTemplateGroup: (container, templateGroupId) =>
        this.searchTemplateGroupService.setListFieldTemplateGroup(container, templateGroupId),
      clearListFieldTemplate: container => this.searchTemplateGroupService.clearListFieldTemplate(container),
      rememberChoice: container => this.contentListChoice.remember(
        container.searchResultViewName, container.listFieldTemplateId),
      reloadOverview: async () => {
        if (this.reloadOverview) {
          await this.reloadOverview();
        }
      },
    };
  }
}
